export interface Vec3 {
  x: number;
  y: number;
  z: number;
}
export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const cloneV3 = (a: Vec3): Vec3 => ({ x: a.x, y: a.y, z: a.z });
export const setV3 = (out: Vec3, x: number, y: number, z: number): Vec3 => {
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
};
export const copyV3 = (out: Vec3, a: Vec3): Vec3 => {
  out.x = a.x;
  out.y = a.y;
  out.z = a.z;
  return out;
};
export const addV3 = (out: Vec3, a: Vec3, b: Vec3): Vec3 => {
  out.x = a.x + b.x;
  out.y = a.y + b.y;
  out.z = a.z + b.z;
  return out;
};
export const subV3 = (out: Vec3, a: Vec3, b: Vec3): Vec3 => {
  out.x = a.x - b.x;
  out.y = a.y - b.y;
  out.z = a.z - b.z;
  return out;
};
export const scaleV3 = (out: Vec3, a: Vec3, s: number): Vec3 => {
  out.x = a.x * s;
  out.y = a.y * s;
  out.z = a.z * s;
  return out;
};
export const dotV3 = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const crossV3 = (out: Vec3, a: Vec3, b: Vec3): Vec3 => {
  const ax = a.x;
  const ay = a.y;
  const az = a.z;
  const bx = b.x;
  const by = b.y;
  const bz = b.z;
  out.x = ay * bz - az * by;
  out.y = az * bx - ax * bz;
  out.z = ax * by - ay * bx;
  return out;
};
export const lengthV3 = (a: Vec3): number => Math.hypot(a.x, a.y, a.z);
export const normalizeV3 = (out: Vec3, a: Vec3): Vec3 => {
  const l = lengthV3(a);
  if (l < 1e-9) {
    out.x = 0;
    out.y = 0;
    out.z = 0;
    return out;
  }
  const inv = 1 / l;
  out.x = a.x * inv;
  out.y = a.y * inv;
  out.z = a.z * inv;
  return out;
};
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const lerpV3 = (out: Vec3, a: Vec3, b: Vec3, t: number): Vec3 => {
  out.x = a.x + (b.x - a.x) * t;
  out.y = a.y + (b.y - a.y) * t;
  out.z = a.z + (b.z - a.z) * t;
  return out;
};
export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const quatIdentity = (): Quat => ({ x: 0, y: 0, z: 0, w: 1 });
export const cloneQuat = (q: Quat): Quat => ({ x: q.x, y: q.y, z: q.z, w: q.w });
export const setQuat = (out: Quat, x: number, y: number, z: number, w: number): Quat => {
  out.x = x;
  out.y = y;
  out.z = z;
  out.w = w;
  return out;
};
export const copyQuat = (out: Quat, q: Quat): Quat => {
  out.x = q.x;
  out.y = q.y;
  out.z = q.z;
  out.w = q.w;
  return out;
};
export const normalizeQuat = (out: Quat, q: Quat): Quat => {
  const l = Math.hypot(q.x, q.y, q.z, q.w);
  if (l < 1e-9) {
    out.x = 0;
    out.y = 0;
    out.z = 0;
    out.w = 1;
    return out;
  }
  const inv = 1 / l;
  out.x = q.x * inv;
  out.y = q.y * inv;
  out.z = q.z * inv;
  out.w = q.w * inv;
  return out;
};
export const quatFromAxisAngle = (out: Quat, axis: Vec3, angle: number): Quat => {
  const half = angle * 0.5;
  const s = Math.sin(half);
  out.x = axis.x * s;
  out.y = axis.y * s;
  out.z = axis.z * s;
  out.w = Math.cos(half);
  return out;
};
export const quatMul = (out: Quat, a: Quat, b: Quat): Quat => {
  const ax = a.x;
  const ay = a.y;
  const az = a.z;
  const aw = a.w;
  const bx = b.x;
  const by = b.y;
  const bz = b.z;
  const bw = b.w;
  out.x = aw * bx + ax * bw + ay * bz - az * by;
  out.y = aw * by - ax * bz + ay * bw + az * bx;
  out.z = aw * bz + ax * by - ay * bx + az * bw;
  out.w = aw * bw - ax * bx - ay * by - az * bz;
  return out;
};
export const quatRotateVec3 = (out: Vec3, q: Quat, v: Vec3): Vec3 => {
  const ix = q.w * v.x + q.y * v.z - q.z * v.y;
  const iy = q.w * v.y + q.z * v.x - q.x * v.z;
  const iz = q.w * v.z + q.x * v.y - q.y * v.x;
  const iw = -q.x * v.x - q.y * v.y - q.z * v.z;
  out.x = ix * q.w + iw * -q.x + iy * -q.z - iz * -q.y;
  out.y = iy * q.w + iw * -q.y + iz * -q.x - ix * -q.z;
  out.z = iz * q.w + iw * -q.z + ix * -q.y - iy * -q.x;
  return out;
};

// Smallest-angle rotation that aligns `from` onto `to` (both unit).
export const quatFromTo = (out: Quat, from: Vec3, to: Vec3): Quat => {
  const d = dotV3(from, to);
  if (d > 0.999999) {
    out.x = 0;
    out.y = 0;
    out.z = 0;
    out.w = 1;
    return out;
  }
  if (d < -0.999999) {
    // 180° flip — pick any perpendicular axis
    const axis = Math.abs(from.x) < 0.9 ? v3(1, 0, 0) : v3(0, 1, 0);
    const tmp = v3();
    crossV3(tmp, from, axis);
    normalizeV3(tmp, tmp);
    out.x = tmp.x;
    out.y = tmp.y;
    out.z = tmp.z;
    out.w = 0;
    return out;
  }
  const tmp = v3();
  crossV3(tmp, from, to);
  out.x = tmp.x;
  out.y = tmp.y;
  out.z = tmp.z;
  out.w = 1 + d;
  return normalizeQuat(out, out);
};

export const slerpQuat = (out: Quat, a: Quat, b: Quat, t: number): Quat => {
  let cos = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
  let bx = b.x;
  let by = b.y;
  let bz = b.z;
  let bw = b.w;
  if (cos < 0) {
    cos = -cos;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let s0: number;
  let s1: number;
  if (cos > 0.9995) {
    s0 = 1 - t;
    s1 = t;
  } else {
    const omega = Math.acos(cos);
    const sinOmega = Math.sin(omega);
    s0 = Math.sin((1 - t) * omega) / sinOmega;
    s1 = Math.sin(t * omega) / sinOmega;
  }
  out.x = a.x * s0 + bx * s1;
  out.y = a.y * s0 + by * s1;
  out.z = a.z * s0 + bz * s1;
  out.w = a.w * s0 + bw * s1;
  return out;
};

export const UP: Readonly<Vec3> = Object.freeze({ x: 0, y: 1, z: 0 });
export const FORWARD: Readonly<Vec3> = Object.freeze({ x: 0, y: 0, z: -1 });
export const RIGHT: Readonly<Vec3> = Object.freeze({ x: 1, y: 0, z: 0 });
